import { useEffect, useState, useCallback } from 'react'
import { useSelector } from 'react-redux'
import api from '../api/api'
import { socket } from '../utilies/config'

// useSellerPresence — Phase G
//
// Tiny hook that returns { isOnline, lastSeen, loading } for a sellerId.
//
// 1. Fetches /seller/:sellerId/presence on mount + when sellerId changes.
// 2. Listens to the global `activeSeller` socket broadcast so the badge
//    flips to "online" / "offline" without polling.
// 3. Re-fetches lastSeen every 5 minutes (idle) so the human-readable
//    "5 dəqiqə əvvəl aktiv idi" text stays fresh.
//
// No Redux changes. The hook is read-only and safe to use on public pages.

const azDigit = (n) => Number(n).toLocaleString('az-AZ')

export const formatLastSeen = (iso) => {
    if (!iso) return 'Son aktivlik məlum deyil'
    const t = new Date(iso).getTime()
    if (!Number.isFinite(t)) return 'Son aktivlik məlum deyil'

    const diff = Date.now() - t
    if (diff < 60_000) return 'İndi aktiv idi'

    const mins = Math.floor(diff / 60_000)
    if (mins < 60) return `${azDigit(mins)} dəqiqə əvvəl aktiv idi`

    const hours = Math.floor(mins / 60)
    if (hours < 24) return `${azDigit(hours)} saat əvvəl aktiv idi`

    const days = Math.floor(hours / 24)
    if (days === 1) return 'Dünən aktiv idi'
    if (days < 7) return `${azDigit(days)} gün əvvəl aktiv idi`

    return new Date(iso).toLocaleDateString('az-AZ')
}

const useSellerPresence = (sellerId) => {
    const [presence, setPresence] = useState({ isOnline: false, lastSeen: null })
    const [loading, setLoading]   = useState(false)
    const { userInfo } = useSelector(s => s.auth)

    const refresh = useCallback(async () => {
        if (!sellerId) return
        setLoading(true)
        try {
            const { data } = await api.get(`/seller/${sellerId}/presence`)
            if (data?.presence) setPresence({
                isOnline: !!data.presence.isOnline,
                lastSeen: data.presence.lastSeen || null,
            })
        } catch (_) {
            // Silent — presence is non-critical
        } finally {
            setLoading(false)
        }
    }, [sellerId])

    useEffect(() => {
        if (!sellerId) return
        let cancelled = false
        refresh()

        // Light refresh every 5 minutes so the human-readable text
        // ("5 dəqiqə əvvəl aktiv idi") stays accurate.
        const t = setInterval(() => { if (!cancelled) refresh() }, 5 * 60 * 1000)
        return () => { cancelled = true; clearInterval(t) }
    }, [sellerId, refresh])

    // Listen to the global activeSeller socket broadcast for live flips.
    // The socket is already connected for logged-in users; guests skip.
    useEffect(() => {
        if (!sellerId || !userInfo) return
        const onActive = (sellers) => {
            const list = Array.isArray(sellers) ? sellers : []
            const online = list.some(s => String(s?.sellerId) === String(sellerId))
            setPresence(prev => {
                if (prev.isOnline === online) return prev
                return {
                    ...prev,
                    isOnline: online,
                    // When seller goes from online -> offline, bump lastSeen
                    // optimistically so the UI updates without a refetch.
                    lastSeen: online ? prev.lastSeen : new Date().toISOString(),
                }
            })
        }
        socket.on('activeSeller', onActive)
        return () => { socket.off('activeSeller', onActive) }
    }, [sellerId, userInfo])

    return {
        ...presence,
        loading,
        statusText: presence.isOnline ? 'Online' : formatLastSeen(presence.lastSeen),
        refresh,
    }
}

export default useSellerPresence
